import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@csa/ui';
import { AuthProvider } from './auth/AuthProvider.js';
import { loadAppConfig } from './lib/app-config.js';
import { ApiError } from './lib/api.js';
import { App } from './App.js';
import { FatalError } from './components/FatalError.js';
import './styles.css';

/**
 * Bootstrap order matters: the runtime configuration has to be in hand before
 * MSAL can be constructed, and MSAL has to exist before any component can ask
 * for a token. Doing the fetch here rather than in an effect means there is
 * never a render pass in which `appConfig()` would throw.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Refetching on focus would fire a burst of queries every time somebody
      // alt-tabs back to a console, and this data is not live enough to earn
      // that. Screens that need live data use SSE.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Retrying a 401, 403 or a validation failure just repeats it. A 429
        // or a 5xx is worth one more attempt.
        if (error instanceof ApiError && error.status < 500 && error.status !== 429) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      // Never retried. A mutation here submits a query for execution or grants
      // an approval; a silent second attempt would double-execute and put two
      // conflicting records in the audit trail.
      retry: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element is missing from the document');

const root = createRoot(container);

loadAppConfig().then(
  (config) => {
    root.render(
      <StrictMode>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider config={config.auth}>
              <App />
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </StrictMode>,
    );
  },
  (err: unknown) => {
    root.render(
      <StrictMode>
        <ThemeProvider>
          <FatalError
            title="Cannot start the console"
            message={
              err instanceof Error
                ? err.message
                : 'The application configuration could not be loaded.'
            }
          />
        </ThemeProvider>
      </StrictMode>,
    );
  },
);
